<script setup lang="ts">
import IcSearch from '@/components/Icons/IcSearch.vue'
import NewDateRangePicker from '@/components/Globals/Inputs/NewDateRangePicker.vue'
import DropdownSensors from '@/components/Globals/Inputs/DropdownSensors.vue'
import SearchBarField from '@/components/Globals/SearchBarField.vue'
import { Form, Field } from 'vee-validate'
import IcAnimateSpin from '@/components/Icons/IcAnimateSpin.vue'
import IcPlus from '@/components/Icons/IcPlus.vue'
import { IRegisterSearchBarSchema } from '@/interfaces/api/schemas/register'
import { ISensor } from '@/interfaces/entities/sensor'
import RegisterSearchBarSchema from '@/schemas/RegisterSearchBarSchema'
import { useRegisterStore } from '@/store/register'
import { formatPeriodDate } from '@/utils/date/date-time'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AdvancedSettingsModal from './AdvancedSettingsModal.vue'
const { t } = useI18n()

const { getPeriodFilter, getAdvancedSettings, isDefaultAdvancedSettings } = useRegisterStore()
const { fetchSensorReadings, fetchSensors, setAdvancedSettings, forceUpdateAdvancedSettings, initializeAdvancedSettings } = useRegisterStore()
const { setSensor, setPeriod } = useRegisterStore()

const formRef = ref<InstanceType<typeof Form>>()
const isLoading = ref(false)
const showAdvancedSettings = ref(false)
const { listSensors } = storeToRefs(useRegisterStore())

const registerStore = useRegisterStore()

//Helpers
const handleSensorSelected = (sensor: ISensor) => {
  setSensor(sensor)
}

const handleAdvancedSettingsUpdate = (settings: { resolution: string; aggregation: string }) => {
  forceUpdateAdvancedSettings(settings)
}

const hasCustomSettings = computed(() => {
  return !isDefaultAdvancedSettings
})

const onSubmit = (values: object) => {
  const castedValues = values as IRegisterSearchBarSchema
  const formattedPeriod = formatPeriodDate(castedValues.period)
  setPeriod(castedValues.period)
  isLoading.value = true
  fetchSensorReadings({
    sensor: castedValues.sensor,
    dateRange: formattedPeriod,
  }).finally(() => {
    isLoading.value = false
  })
}

onMounted(() => {
  fetchSensors()
  initializeAdvancedSettings()
})
</script>

<template>
  <div class="mt-0 flex flex-col flex-wrap items-start gap-3 bg-white p-4 shadow-md sm:flex-row sm:items-end">
    <Form ref="formRef" :validation-schema="RegisterSearchBarSchema" @submit="onSubmit" class="flex w-full flex-col flex-wrap gap-3 sm:flex-row sm:items-end">
      <SearchBarField :label="t('sensors.name')">
        <DropdownSensors name="sensor" :options="listSensors" @option-selected="handleSensorSelected" />
      </SearchBarField>

      <SearchBarField :label="t('dashboard.header.buttons.period')" class="min-w-[320px] flex-1">
        <Field name="period" v-slot="{ field }">
          <NewDateRangePicker v-bind="field" :already-selected-period="getPeriodFilter" :placeholder="t('plugins.vue_datepicker.placeholder')" name="period" class="w-full" />
        </Field>
      </SearchBarField>
      <button
        @click="showAdvancedSettings = true"
        type="button"
        class="flex h-12 shrink-0 items-center justify-center border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue focus:ring-offset-2"
        :class="{ 'border-blue bg-blue-50 text-blue': hasCustomSettings }"
        :title="t('registers.advancedSettings.title')">
        <IcPlus class="mr-1 h-4 w-4" />
        {{ t('registers.advancedSettings.title') }}
        <span v-if="hasCustomSettings" class="ml-1 h-2 w-2 bg-blue"></span>
      </button>

      <div class="ml-auto flex shrink-0 items-center">
        <button
          :disabled="isLoading"
          class="flex flex-row items-center bg-blue px-4 py-3 text-white hover:bg-blue-2 disabled:opacity-40 disabled:hover:bg-blue">
          <IcSearch v-if="!isLoading" class="mr-2 h-5 w-5" />
          <span v-else class="mr-2 animate-spin">
            <IcAnimateSpin class="h-5 w-5 text-white" />
          </span>
          {{ t('dashboard.header.buttons.search') }}
        </button>
      </div>
    </Form>
  </div>

  <!-- Modal de Configurações Avançadas -->
  <AdvancedSettingsModal v-model="showAdvancedSettings" :settings="getAdvancedSettings" @update:settings="handleAdvancedSettingsUpdate" />
</template>
